/**
 * Per-user usage table for the Reports tab. Sorted by events desc (the heaviest hosts first — the
 * "Top N" intent) with the shared Table's records-per-page control + pagination. Columns adapt to the
 * Type filter (Meetings / Webinars columns shown only when relevant).
 */

import { useTranslation } from "react-i18next";
import { Table, type Column, EmptyState } from "@/components/ui";
import { formatInt } from "@/lib/format";
import type { ReportTopUser, ReportType } from "@/api/types";

export function ReportTopUsersTable({ users, type }: { users: ReportTopUser[]; type: ReportType }) {
  const { t } = useTranslation();
  if (users.length === 0) {
    return <EmptyState title={t("customer.reports.noUsers")} />;
  }

  const columns: Column<ReportTopUser>[] = [
    {
      key: "user",
      header: t("customer.reports.user"),
      cell: (r) => (
        <span className="flex flex-col">
          <span className="font-medium text-(--text)">{r.host_display_name ?? r.host_email}</span>
          {r.host_display_name && (
            <span className="text-(length:--text-2xs) text-(--text-subtle)">{r.host_email}</span>
          )}
        </span>
      ),
      sortValue: (r) => r.host_display_name ?? r.host_email,
    },
    ...(type !== "webinars"
      ? [
          {
            key: "meetings",
            header: t("customer.reports.kpiMeetings"),
            cell: (r: ReportTopUser) => formatInt(r.meetings),
            sortValue: (r: ReportTopUser) => r.meetings,
            align: "right" as const,
            numeric: true,
          },
        ]
      : []),
    ...(type !== "meetings"
      ? [
          {
            key: "webinars",
            header: t("customer.reports.kpiWebinars"),
            cell: (r: ReportTopUser) => formatInt(r.webinars),
            sortValue: (r: ReportTopUser) => r.webinars,
            align: "right" as const,
            numeric: true,
          },
        ]
      : []),
    {
      key: "participants",
      header: t("customer.reports.kpiParticipants"),
      cell: (r) => formatInt(r.participants),
      sortValue: (r) => r.participants,
      align: "right",
      numeric: true,
    },
    {
      key: "minutes",
      header: t("customer.reports.kpiMinutes"),
      cell: (r) => formatInt(r.minutes),
      sortValue: (r) => r.minutes,
      align: "right",
      numeric: true,
    },
  ];

  return (
    <Table
      columns={columns}
      rows={users}
      rowKey={(r) => r.host_email}
      caption={t("customer.reports.perUser")}
    />
  );
}
